import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MobilisationEvent } from '@hcc/types';
import { getSocket } from '../lib/socket';
import { useCurrentUser } from './useAuth';

/** Task lifecycle events that can change the awaiting-review queue. */
const TASK_EVENT_TYPES = new Set<MobilisationEvent['event_type']>([
  'TaskAssigned',
  'TaskUpdated',
  'TaskSubmitted',
  'TaskApproved',
  'TaskRejected',
  'TaskRemoved',
  'CheckProcessed',
]);

/**
 * Subscribe the current React tree to server-pushed event-appended
 * notifications. On every push we invalidate the relevant queries:
 *   - All people lists (any filter combination)
 *   - The affected person's detail and event history
 *
 * TanStack Query then refetches the queries that components are
 * actually mounted on. The socket is a singleton; this hook only
 * registers and tears down its listener.
 *
 * Auth: the underlying socket reuses the express-session cookie. If
 * the user is not signed in, we don't connect.
 */
export function useRealtime(): void {
  const { data: user } = useCurrentUser();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;
    const socket = getSocket();
    // A connection denied by the server's auth middleware (e.g. the
    // socket was created before this login) is not retried by
    // socket.io, so revive it now that a session exists. No-op when
    // already connected or connecting.
    if (!socket.connected) socket.connect();

    const onEvent = (ev: MobilisationEvent) => {
      qc.invalidateQueries({ queryKey: ['people'] });
      qc.invalidateQueries({ queryKey: ['people', ev.person_id] });
      qc.invalidateQueries({ queryKey: ['people', ev.person_id, 'events'] });
      // Keep the "Tasks to review" queue and its nav counter live: any task
      // lifecycle event can add to or clear the awaiting-review set.
      if (TASK_EVENT_TYPES.has(ev.event_type)) {
        qc.invalidateQueries({ queryKey: ['admin', 'tasks', 'review'] });
        // The screening-check lists (DBS / DVLA) project from the same
        // task events plus CheckProcessed.
        qc.invalidateQueries({ queryKey: ['admin', 'checks'] });
        // The onboarding dashboard's progress roll-up derives from the
        // same task lifecycle, so HR sees submissions and approvals land
        // while watching. Draft autosaves are metadata writes, not
        // events, so the started-a-form signal still waits for a refetch.
        qc.invalidateQueries({ queryKey: ['onboarding-people'] });
        qc.invalidateQueries({ queryKey: ['person-onboarding', ev.person_id] });
      }
      // Kit issues and returns flip tracked assets server-side, so the
      // asset lists, the drawer's pre-assigned/pool pickers, the
      // person's outstanding-kit seed, and the reports must refetch or
      // a second desk can still select an asset another desk just
      // issued. The Kit-to-order board is deliberately NOT here: it
      // derives from form submissions and order marks, never from
      // events, and its refetch is the heaviest query in the app.
      if (ev.event_type === 'KitIssued' || ev.event_type === 'KitReturned') {
        qc.invalidateQueries({ queryKey: ['assets'] });
        qc.invalidateQueries({ queryKey: ['station-kit', ev.person_id] });
        qc.invalidateQueries({ queryKey: ['reports'] });
      }
      // The training matrix moves only on attempts, certificate issues and
      // (un)assignment — not the wider task lifecycle, whose events fire
      // constantly during a busy onboarding afternoon and would refetch
      // the heaviest aggregate for no visible change.
      if (
        ev.event_type === 'TrainingAttemptCompleted' ||
        ev.event_type === 'TrainingCertificateIssued' ||
        ev.event_type === 'TaskAssigned' ||
        ev.event_type === 'TaskRemoved'
      ) {
        qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] });
      }
    };

    // Broadcasts missed while the socket was down (server restart, proxy
    // timeout, wifi blip) are gone for good, so a successful reconnect
    // must treat the whole cache as suspect. The manager-level
    // 'reconnect' fires only after a drop, never on first connect, so
    // initial page load doesn't double-fetch. A browser 'online' event
    // does not cover this: the socket can die while the OS stays online.
    const onReconnect = () => {
      qc.invalidateQueries();
    };

    socket.on('event:appended', onEvent);
    socket.io.on('reconnect', onReconnect);
    return () => {
      socket.off('event:appended', onEvent);
      socket.io.off('reconnect', onReconnect);
    };
  }, [user, qc]);
}
